import React, { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import useEventTracking from '@hooks/useEventTracking';
import { impression } from '@tracking';
import { capitalizeTopic } from '@utils/formatting';

import ForYouReadingStat from './ReadingStat';
import ForYouSection from './Section';
import { EMPTY_RESULTS, FOR_YOU_ENGAGEMENT_LABELS, FOR_YOU_SECTION_IDS } from './constants';
import type { ForYouReadingStatsProps } from './interfaces';

const READING_STAT_MIN_VALUE = 16;

const ForYouReadingStats = ({
  articlesRead,
  // minutesRead,
  mostReadTopic,
  mostReadNewsletters,
  newsletterNames,
}: ForYouReadingStatsProps) => {
  const { trackEvent } = useEventTracking();

  /**
   * Data transformation to get the most read newsletter
   * Data comes back in the form of ['newsletter_id: count', ...], so
   * we need to get the largest count and that newsletter's name
   */
  const mostReadNewsletter = useMemo(() => {
    return mostReadNewsletters?.reduce(
      (acc, pair) => {
        const [newsletterId, count] = pair.split(': ');

        if (acc.count < Number(count)) {
          acc.count = Number(count);
          acc.newsletter = newsletterNames?.[newsletterId] || '';
        }

        return acc;
      },
      { count: 0, newsletter: '' }
    );
  }, [mostReadNewsletters, newsletterNames]);

  const capitalizedTopic = useMemo(() => capitalizeTopic(mostReadTopic || ''), [mostReadTopic]);

  const normalizedArticlesRead = useMemo(() => {
    if (!articlesRead) return 0;

    // Some people's data is wonky with semicolons like this: '16;10'
    const splitNumber = articlesRead.split(';');
    if (splitNumber.length === 1) return Number(splitNumber[0]);

    return Math.max(...splitNumber.map(Number));
  }, [articlesRead]);

  if (
    normalizedArticlesRead < READING_STAT_MIN_VALUE &&
    // !minutesRead &&
    !mostReadTopic &&
    !mostReadNewsletter?.newsletter
  ) {
    trackEvent(
      impression({ impressionElement: FOR_YOU_SECTION_IDS.ReadingStats, context: EMPTY_RESULTS })
    );
    return null;
  }

  return (
    <ForYouSection
      id={FOR_YOU_SECTION_IDS.ReadingStats}
      className="[&_+_nav]:mt-48"
      highlight={FOR_YOU_ENGAGEMENT_LABELS.Subscribed.ReadingStats.label}
      subtitle={FOR_YOU_ENGAGEMENT_LABELS.Subscribed.ReadingStats.subtitle}
      title={FOR_YOU_ENGAGEMENT_LABELS.Subscribed.ReadingStats.title}
      disableBorder
    >
      <InView
        onChange={(inView) => {
          inView && trackEvent(impression({ impressionElement: FOR_YOU_SECTION_IDS.ReadingStats }));
        }}
        triggerOnce
      >
        <div className="items-between relative mt-48 flex flex-col justify-center gap-32 sm:flex-row sm:flex-wrap md:flex-nowrap lg:gap-64">
          {normalizedArticlesRead >= READING_STAT_MIN_VALUE && (
            <ForYouReadingStat index={0} name="Articles Read" value={normalizedArticlesRead} />
          )}
          {/**
           * TODO: Waiting for data to be normalized before displaying. No ticket yet.
           * {minutesRead && (
            <ForYouReadingStat index={1} name="Time Spent" value={`${minutesRead} mins`} />
          )} */}
          {mostReadTopic && (
            <ForYouReadingStat index={2} name="Top Topic" value={capitalizedTopic} small />
          )}
          {mostReadNewsletter?.newsletter && (
            <ForYouReadingStat
              index={3}
              name="Most-Read Newsletter"
              value={mostReadNewsletter.newsletter}
              small
            />
          )}
        </div>
      </InView>
    </ForYouSection>
  );
};

export default ForYouReadingStats;
